// File: InvoicePrint.jsx — صفحة طباعة الفاتورة
// شريط واحد: اختيار القالب + طباعة + PDF + مشاركة — والفاتورة ببيانات بطاقة الشركة والعميل كاملة
import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Paper, TextField, MenuItem, Button, Typography, CircularProgress, ListItemText } from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import StarIcon from "@mui/icons-material/Star";
import { toast } from "react-toastify";
import PrintLayoutRenderer from "../../components/PrintDesigner/PrintLayoutRenderer";
import { loadInvoiceForPrint, useInvoiceTemplates, invoicePdf, sharePdfBlob } from "../../components/PrintDesigner/printData";
import { paperLabel } from "../../components/PrintDesigner/printTemplates";

const ACCENT = "#4F46E5";

export default function InvoicePrint() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const autoPrint = searchParams.get("auto") === "true";
  const [data, setData] = useState(null);
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    (async () => {
      try {
        const res = await loadInvoiceForPrint(id);
        if (!alive) return;
        setData(res.data);
        setLayout(res.layout);
      } catch (err) {
        console.error("🛑 خطأ في تحميل الفاتورة:", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", minHeight: "60vh", gap: 1 }}>
        <CircularProgress sx={{ color: ACCENT }} />
        <Typography sx={{ color: "#64748b" }}>جاري تحميل الفاتورة...</Typography>
      </Box>
    );
  }
  if (!data) return <Typography sx={{ p: 4, textAlign: "center", color: "#e11d48" }}>تعذر تحميل بيانات الفاتورة</Typography>;

  return <PrintView data={data} layout={layout} autoPrint={autoPrint} />;
}

// ======================================================================
function PrintView({ data, layout, autoPrint }) {
  const { options, current, pick } = useInvoiceTemplates(layout, data.invoiceTypeId ?? data.invoiceType);
  const pageRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const printed = useRef(false);

  const handlePrint = () => window.print();

  // الطباعة التلقائية بالقالب الافتراضي
  useEffect(() => {
    if (!autoPrint || printed.current) return;
    printed.current = true;
    const t = setTimeout(() => window.print(), 600);
    return () => clearTimeout(t);
  }, [autoPrint]);

  const buildPdf = () => invoicePdf(pageRef.current, current, data);

  const handlePdf = async () => {
    setBusy(true);
    try { await buildPdf().save(); } catch { toast.error("فشل إنشاء ملف PDF"); } finally { setBusy(false); }
  };

  const handleShare = async () => {
    setBusy(true);
    try {
      const blob = await buildPdf().output("blob");
      const shared = await sharePdfBlob(blob, data.invoiceNumber);
      if (!shared) toast.info("تم تحميل الملف لأن المشاركة غير مدعومة في هذا المتصفح");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("فشل مشاركة الفاتورة");
    } finally {
      setBusy(false);
    }
  };

  const btn = { borderRadius: "10px", fontWeight: 700, height: 40, px: 2, whiteSpace: "nowrap", "& .MuiButton-startIcon": { ml: .75, mr: -.25 } };

  return (
    <Box dir="rtl" sx={{ p: { xs: 1, sm: 2 }, bgcolor: "#eef0f4", minHeight: "100vh", "@media print": { p: 0, bgcolor: "#fff", minHeight: 0 } }}>
      <style>{`
        @media print {
          body { margin: 0; padding: 0; }
          .print-hidden { display: none !important; }
          #invoice-template-container { margin: 0 !important; box-shadow: none !important; }
        }
      `}</style>

      {/* شريط واحد: القالب + الأزرار */}
      <Paper
        elevation={0} className="print-hidden" data-html2canvas-ignore="true"
        sx={{
          position: "sticky", top: 8, zIndex: 5, maxWidth: 900, mx: "auto", mb: 2, p: 1,
          display: "flex", alignItems: "center", gap: 1, flexWrap: { xs: "wrap", sm: "nowrap" },
          borderRadius: "14px", border: "1px solid #e5e7eb", boxShadow: "0 6px 18px -10px rgba(15,23,42,.3)",
        }}
      >
        <TextField
          select size="small" label="قالب الطباعة" value={current?.id || ""} onChange={e => pick(e.target.value)}
          sx={{ flex: 1, minWidth: 220, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontWeight: 700 } }}
          SelectProps={{
            renderValue: (v) => {
              const o = options.find(x => x.id === v);
              return o ? `${o.name} — ${paperLabel(o.layout)}` : "";
            },
          }}
        >
          {options.map(o => (
            <MenuItem key={o.id} value={o.id} sx={{ gap: 1 }}>
              <ListItemText primary={o.name} secondary={paperLabel(o.layout)} primaryTypographyProps={{ fontWeight: 700, fontSize: 14 }} />
              {o.isDefault && <StarIcon sx={{ fontSize: 18, color: "#f59e0b" }} />}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" startIcon={<PrintOutlinedIcon />} onClick={handlePrint} disabled={busy}
          sx={{ ...btn, bgcolor: ACCENT, "&:hover": { bgcolor: "#4338CA" } }}>
          طباعة
        </Button>
        <Button variant="outlined" startIcon={busy ? <CircularProgress size={16} /> : <PictureAsPdfOutlinedIcon />} onClick={handlePdf} disabled={busy}
          sx={{ ...btn, color: ACCENT, borderColor: "#c7d2fe" }}>
          PDF
        </Button>
        <Button variant="outlined" startIcon={<ShareOutlinedIcon />} onClick={handleShare} disabled={busy}
          sx={{ ...btn, color: ACCENT, borderColor: "#c7d2fe" }}>
          مشاركة
        </Button>
      </Paper>

      {/* الفاتورة */}
      <Box id="invoice-template-container" sx={{ display: "flex", justifyContent: "center" }}>
        <Box sx={{ boxShadow: "0 10px 30px -12px rgba(15,23,42,.35)", "@media print": { boxShadow: "none" } }}>
          {current && <PrintLayoutRenderer key={current.id} ref={pageRef} layout={current.layout} data={data} />}
        </Box>
      </Box>
    </Box>
  );
}